import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import service from '@/services';
import useWorkspaceStore from '@/stores/WorkspaceStore';
import { SOURCE_TYPE } from '@/types/feedbackConst';

import { getIntegrationQueryKeys } from '../../integrationUtils';
import {
  IMPORT_STATE,
  type ImportHistoryItemT,
  type StampedInfo,
} from '../types';

const mapStampedInfo = (data: unknown): StampedInfo | null => {
  if (!data || typeof data !== 'object') return null;

  const info = data as {
    workspace_id?: string;
    alias?: string;
    state?: IMPORT_STATE;
    import_state?: IMPORT_STATE;
    history?: ImportHistoryItemT[];
  };

  if (!info.alias) return null;

  return {
    workspace_id: info.workspace_id || info.alias,
    alias: info.alias,
    import_state: info.import_state || info.state || IMPORT_STATE.NOT_STARTED,
    history: info.history || [],
  };
};

export const useStamped = (enabled: boolean) => {
  const queryClient = useQueryClient();
  const currWorkspace = useWorkspaceStore((state) => state.currWorkspace);
  const workspaceAlias = currWorkspace?.alias;

  if (!workspaceAlias) throw new Error('Workspace alias is required');

  const queryKey = getIntegrationQueryKeys(SOURCE_TYPE.STAMPED, workspaceAlias);

  const { data: stampedInfo } = useQuery({
    queryKey,
    queryFn: async () => {
      const data = await service.stamped.getStampedInfo(workspaceAlias);
      return mapStampedInfo(data);
    },
    enabled,
  });

  const stampedAlias = stampedInfo?.alias ?? '';

  const { mutate: disconnectStampedMutation } = useMutation({
    mutationFn: () => service.stamped.deleteStampedInfo(stampedAlias),
    onSuccess: () => queryClient.invalidateQueries({ queryKey }),
  });

  const { mutate: requestImportMutation } = useMutation({
    mutationFn: ({ start_date, end_date }: NullableDateRangeT) => {
      if (!start_date || !end_date || !stampedAlias) {
        return Promise.resolve(false);
      }

      const startDate: string = start_date;
      const endDate: string = end_date;

      return service.stamped.importWorkspaceFeedback(
        stampedAlias,
        startDate,
        endDate,
      );
    },
    onSuccess: () => {
      queryClient.setQueryData<StampedInfo | null>(queryKey, (current) => {
        if (!current) return current;

        return {
          ...current,
          import_state: IMPORT_STATE.IMPORTING,
        };
      });
    },
  });

  return {
    stampedWorkspace: stampedInfo,
    disconnectStampedMutation,
    requestImportMutation,
    setStampedWorkspace: (workspace: StampedInfo) => {
      queryClient.setQueryData<StampedInfo | null>(queryKey, workspace);
    },
  };
};
